import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { projectsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { LogOut, MessageSquare, User, Loader2, Archive, RotateCcw, Users } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import ProjectDetailPanel from './ProjectDetailPanel';
import { MessagesDialog } from './MessagesDialog';
import { CalendarPanel } from './CalendarPanel';
import { QuotesPanel } from './QuotesPanel';
import { SuppliesPanel } from './SuppliesPanel';
import { ResourcesPanel } from './ResourcesPanel';
import { CreateProjectDialog } from './CreateProjectDialog';
import { EditProjectDialog } from './EditProjectDialog';
import { ManageTeamDialog } from './ManageTeamDialog';
import { ActiveOrdersPanel } from './ActiveOrdersPanel';
import { APP_VERSION } from '../version';
import { useDashboardData, useCreateProject, useUpdateProject, useDeleteProject, useMoveStage, useMessages } from '../hooks/useDashboard';

const DEFAULT_STAGES = [
  { id: 'design', name: 'Design', order: 0, color: '#4A9D6E' },
  { id: 'ready-to-cut', name: 'Ready to Cut', order: 1, color: '#D4855A' },
  { id: 'ready-for-paint', name: 'Ready for Paint', order: 2, color: '#E5A84B' },
  { id: 'ready-for-assembly', name: 'Ready for Assembly', order: 3, color: '#3E8A6E' },
  { id: 'install', name: 'Install', order: 4, color: '#6B7280' },
];

function buildStages(projects) {
  // Separate quotes from active projects
  const activeProjects = projects.filter(p => p.status !== 'quote');
  const quoteProjects = projects.filter(p => p.status === 'quote');

  const stageMap = {};
  const allStages = new Set();
  activeProjects.forEach(project => {
    (project.stages || []).forEach(stage => {
      allStages.add(JSON.stringify({ id: stage.id, name: stage.name, color: stage.color, order: stage.order }));
    });
  });
  const parsed = allStages.size > 0
    ? Array.from(allStages).map(s => JSON.parse(s)).sort((a, b) => a.order - b.order)
    : DEFAULT_STAGES;

  parsed.forEach(stage => {
    stageMap[stage.name] = {
      ...stage,
      projects: activeProjects.filter(p => {
        const currentStage = (p.stages || []).find(s => s.id === p.current_stage_id);
        return currentStage?.name === stage.name;
      })
    };
  });

  return {
    quotes: { projects: quoteProjects },
    workflow: Object.values(stageMap)
  };
}

// ---- CreateProjectDialog and EditProjectDialog extracted to their own files ----

function DashboardHeader({ user, logout, unreadCount, onOpenMessages, onOpenTeam }) {
  return (
    <header className="bg-[#23272E] border-b border-[#33383F] px-4 md:px-8 py-3 md:py-4">
      <div className="flex items-center justify-between">
        <div>
          <img
            src="https://customer-assets.emergentagent.com/job_project-builder-215/artifacts/n5lbvytv_IMG_9406-no-bg%20v2%28RESIZE%29.png"
            alt="Roebuck Woodworks"
            className="h-8 md:h-10 object-contain"
          />
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          {user?.role === 'admin' && (
            <Button variant="outline" className="border-[#4A9D6E]/50 bg-[#4A9D6E]/10 hover:bg-[#4A9D6E]/20 text-[#4A9D6E] rounded-xl"
              onClick={onOpenTeam} data-testid="manage-team-button" title="Manage Team">
              <Users className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Team</span>
            </Button>
          )}
          <Button variant="outline" size="icon" className="relative border-[#33383F] hover:bg-[#2C3038] rounded-xl"
            onClick={onOpenMessages} data-testid="open-messages-button">
            <MessageSquare className="w-5 h-5 text-[#9CA3AF]" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#D4855A] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 hover:bg-[#2C3038] rounded-xl" data-testid="user-menu-button">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-[#4A9D6E] text-white">{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                </Avatar>
                <span className="hidden md:inline text-[#F0EDE8] font-medium">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#23272E] border-[#33383F]">
              <DropdownMenuItem className="text-[#9CA3AF]" data-testid="user-email-item">
                <User className="w-4 h-4 mr-2" />
                <div className="flex flex-col">
                  <span>{user?.email}</span>
                  <span className="text-[10px] uppercase tracking-wide" data-testid="user-role-badge">
                    role: <span className={user?.role === 'admin' ? 'text-[#4A9D6E] font-bold' : 'text-[#DC2626] font-bold'}>{user?.role || 'unknown'}</span>
                  </span>
                </div>
              </DropdownMenuItem>
              {user?.role === 'admin' && (
                <DropdownMenuItem onClick={onOpenTeam} className="text-[#F0EDE8]" data-testid="manage-team-menu-item">
                  <Users className="w-4 h-4 mr-2" />Manage Team
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={logout} className="text-[#E55B5B] focus:text-[#E55B5B]" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />Sign Out
              </DropdownMenuItem>
              <div className="px-2 py-1.5 text-[10px] text-[#555D68] font-mono border-t border-[#33383F] mt-1" data-testid="app-version">
                {APP_VERSION}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const { projects, users, conversations, unreadCount, loading, fetchData } = useDashboardData();
  const handleCreateProject = useCreateProject(fetchData);
  const handleUpdateProject = useUpdateProject(fetchData);
  const handleDeleteProject = useDeleteProject(fetchData);
  const handleMoveStage = useMoveStage(fetchData);
  const { selectedChat, messages, newMessage, setNewMessage, handleOpenChat, handleSendMessage } = useMessages(fetchData);

  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [showManageTeam, setShowManageTeam] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [detailProject, setDetailProject] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { quotes } = useMemo(() => buildStages(projects), [projects]);
  const otherUsers = useMemo(() => users.filter(u => u.id !== user?.id), [users, user]);

  const handleCardClick = useCallback((project) => {
    setDetailProject(project);
    setShowDetailPanel(true);
  }, []);

  const handleEditClick = useCallback((project) => {
    setShowDetailPanel(false);
    setDetailProject(null);
    setSelectedProject(project);
    setShowEditProject(true);
  }, []);

  const handleDetailClose = useCallback(() => {
    setShowDetailPanel(false);
    setDetailProject(null);
  }, []);

  const handleDetailUpdate = useCallback(() => {
    fetchData().then(() => {
      if (detailProject) {
        projectsApi.getOne(detailProject.id).then(res => setDetailProject(res.data)).catch(() => {});
      }
    });
  }, [fetchData, detailProject]);

  const fetchArchivedProjects = useCallback(async () => {
    setLoadingArchived(true);
    try {
      const res = await projectsApi.getAll(true);
      setArchivedProjects(res.data);
    } catch (err) { void err; }
    finally { setLoadingArchived(false); }
  }, []);

  const handleToggleArchived = useCallback(() => {
    if (!showArchived) fetchArchivedProjects();
    setShowArchived(prev => !prev);
  }, [showArchived, fetchArchivedProjects]);

  const handleUnarchive = useCallback(async (projectId) => {
    try {
      await projectsApi.unarchive(projectId);
      toast.success('Project restored to board');
      fetchArchivedProjects();
      fetchData();
    } catch (err) {
      toast.error('Failed to restore project');
      void err;
    }
  }, [fetchArchivedProjects, fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1D21] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A9D6E]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1D21]" data-testid="dashboard">
      <Toaster position="top-right" richColors />

      <DashboardHeader user={user} logout={logout} unreadCount={unreadCount}
        onOpenMessages={() => setShowMessages(true)}
        onOpenTeam={() => setShowManageTeam(true)} />

      <main className="p-4 md:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-[#F0EDE8] tracking-tight">
              {showArchived ? 'Archived Projects' : 'Project Board'}
            </h2>
            <p className="text-sm text-[#9CA3AF]">
              {showArchived ? `${archivedProjects.length} completed projects` : `${projects.length} active projects`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleToggleArchived}
              className={`border-[#33383F] rounded-xl ${showArchived ? 'bg-[#4A9D6E] text-white hover:bg-[#3D8A5E] border-[#4A9D6E]' : 'text-[#9CA3AF] hover:bg-[#2C3038]'}`}
              data-testid="toggle-archived-button">
              <Archive className="w-4 h-4 mr-1.5" />
              {showArchived ? 'Board' : 'Archived'}
            </Button>
            {!showArchived && (
              <CreateProjectDialog open={showCreateProject} onOpenChange={setShowCreateProject}
                onCreate={handleCreateProject} />
            )}
          </div>
        </div>

        {/* Top Row: Calendar | Quotes | Supplies | Resources */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <CalendarPanel onClose={() => {}} refreshKey={projects.map(p => `${p.id}:${p.target_completion_date || ''}`).join('|')} />
          <QuotesPanel quotes={quotes} onCardClick={handleCardClick} onRefresh={fetchData} />
          <SuppliesPanel />
          <ResourcesPanel />
        </div>

        {showArchived ? (
          <div className="space-y-3" data-testid="archived-projects-list">
            {loadingArchived ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#4A9D6E]" />
              </div>
            ) : archivedProjects.length === 0 ? (
              <div className="text-center py-16 text-[#9CA3AF]">
                <Archive className="w-12 h-12 mx-auto mb-3 text-[#555D68]" />
                <p className="font-medium">No archived projects</p>
                <p className="text-sm mt-1">Completed projects will appear here</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {archivedProjects.map((project) => (
                  <div key={project.id}
                    className="bg-[#23272E] border border-[#33383F] rounded-2xl p-4 shadow-sm"
                    data-testid={`archived-card-${project.id}`}>
                    <p className="accent-text text-[#9CA3AF] mb-1">{project.client_name}</p>
                    <h4 className="font-semibold text-[#F0EDE8] tracking-tight mb-2">{project.title}</h4>
                    {project.description && (
                      <p className="text-sm text-[#9CA3AF] mb-3 line-clamp-2">{project.description}</p>
                    )}
                    <div className="flex gap-1 mb-3">
                      {(project.stages || []).map((s) => (
                        <div key={s.id} className="h-1.5 flex-1 rounded-full" style={{ backgroundColor: s.color }} />
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-[#33383F]">
                      <span className="text-xs text-[#9CA3AF] font-mono">
                        Completed {project.archived_at ? new Date(project.archived_at).toLocaleDateString() : ''}
                      </span>
                      <Button variant="outline" size="sm" onClick={() => handleUnarchive(project.id)}
                        className="border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl"
                        data-testid={`unarchive-${project.id}`}>
                        <RotateCcw className="w-3 h-3 mr-1" />Restore
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <ActiveOrdersPanel projects={projects} onCardClick={handleCardClick}
            onEdit={handleEditClick} onDelete={handleDeleteProject} onMoveStage={handleMoveStage} />
        )}
      </main>

      <EditProjectDialog open={showEditProject} onOpenChange={setShowEditProject}
        project={selectedProject} onUpdate={handleUpdateProject} onDelete={handleDeleteProject}
        onArchived={fetchData} />

      <MessagesDialog open={showMessages} onOpenChange={setShowMessages}
        otherUsers={otherUsers} conversations={conversations} selectedChat={selectedChat}
        messages={messages} newMessage={newMessage} setNewMessage={setNewMessage}
        onOpenChat={handleOpenChat} onSendMessage={handleSendMessage} currentUserId={user?.id} />

      <ManageTeamDialog open={showManageTeam} onOpenChange={setShowManageTeam} currentUserId={user?.id} />

      <ProjectDetailPanel project={detailProject} open={showDetailPanel}
        onClose={handleDetailClose} onUpdate={handleDetailUpdate} />
    </div>
  );
}
